import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import {
  FiAlertTriangle,
  FiXCircle,
  FiClock,
  FiPhoneCall,
  FiPlay,
  FiCheckCircle,
} from "react-icons/fi";
import "../../styles/UnverifiedTripModal.css";

const UnverifiedTripModal = ({
  show,
  onClose,
  tripId = "",
  vehicleIssues = [],
  driverDocIssues = [],
  canStartUnverified = false,
  onProceedOverride = null,
  loading = false,
}) => {
  const hasVehicleIssues = Array.isArray(vehicleIssues) && vehicleIssues.length > 0;
  const hasDriverIssues = Array.isArray(driverDocIssues) && driverDocIssues.length > 0;
  const totalIssuesCount = (vehicleIssues?.length || 0) + (driverDocIssues?.length || 0);

  // Lock background scrolling completely when modal is open
  useEffect(() => {
    if (!show) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.classList.add("unverified_modal_open");
    document.documentElement.classList.add("unverified_modal_open");

    document.body.style.setProperty("overflow", "hidden", "important");
    document.documentElement.style.setProperty("overflow", "hidden", "important");

    // Prevent scrolling events outside the modal content (both touch and wheel)
    const handleTouchMove = (e) => {
      if (!e.target.closest(".unverified_trip_modal_body")) {
        e.preventDefault();
      }
    };

    const handleWheel = (e) => {
      if (!e.target.closest(".unverified_trip_modal_body")) {
        e.preventDefault();
      }
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.body.classList.remove("unverified_modal_open");
      document.documentElement.classList.remove("unverified_modal_open");

      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;

      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("wheel", handleWheel);
    };
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={true}
      dialogClassName="unverified_trip_modal"
    >
      <Modal.Body className="unverified_trip_modal_body">
        {/* Close Icon */}
        <button
          type="button"
          className="unverified_modal_close_btn"
          onClick={onClose}
          aria-label="Close"
          disabled={loading}
        >
          <IoClose />
        </button>

        {/* Warning Icon */}
        <div className="unverified_icon_badge">
          <FiAlertTriangle />
        </div>

        {/* Title & Subtitle */}
        <h3 className="unverified_modal_title">Checklist Incomplete</h3>
        <p className="unverified_modal_meta">
          {tripId ? <span className="trip_ref">Trip #{tripId}</span> : null}
          {tripId ? <span className="meta_sep">•</span> : null}
          <span className="meta_status">Cannot start trip</span>
        </p>

        {/* Clean Issues Card */}
        <div className="unverified_issues_card">
          {hasVehicleIssues && (
            <div className="unverified_group">
              <div className="unverified_group_header">
                <span className="unverified_group_title">Vehicle Inspection</span>
                <span className="unverified_group_tag">
                  {vehicleIssues.length} {vehicleIssues.length === 1 ? "issue" : "issues"}
                </span>
              </div>
              <div className="unverified_group_items">
                {vehicleIssues.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className={`unverified_row ${item.type === "failed" ? "row_failed" : "row_missing"}`}
                  >
                    <div className="unverified_row_top">
                      <div className="unverified_row_title_wrap">
                        {item.type === "failed" ? (
                          <FiXCircle className="status_ico ico_fail" />
                        ) : (
                          <FiClock className="status_ico ico_pending" />
                        )}
                        <span className="unverified_row_title">{item.label || item.title}</span>
                      </div>
                      <span
                        className={`status_pill ${
                          item.type === "failed" ? "pill_fail" : "pill_pending"
                        }`}
                      >
                        {item.statusText || (item.type === "failed" ? "Failed" : "Incomplete")}
                      </span>
                    </div>
                    {item.notes ? (
                      <div className="unverified_row_reason">
                        <span className="reason_tag">Reason:</span>
                        <span className="reason_text">{item.notes}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasDriverIssues && (
            <div className="unverified_group">
              <div className="unverified_group_header">
                <span className="unverified_group_title">Driver Documents</span>
                <span className="unverified_group_tag">
                  {driverDocIssues.length} {driverDocIssues.length === 1 ? "issue" : "issues"}
                </span>
              </div>
              <div className="unverified_group_items">
                {driverDocIssues.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className={`unverified_row ${item.type === "failed" ? "row_failed" : "row_missing"}`}
                  >
                    <div className="unverified_row_top">
                      <div className="unverified_row_title_wrap">
                        {item.type === "failed" ? (
                          <FiXCircle className="status_ico ico_fail" />
                        ) : (
                          <FiClock className="status_ico ico_pending" />
                        )}
                        <span className="unverified_row_title">{item.label || item.title}</span>
                      </div>
                      <span
                        className={`status_pill ${
                          item.type === "failed" ? "pill_fail" : "pill_pending"
                        }`}
                      >
                        {item.statusText || (item.type === "failed" ? "Failed" : "Incomplete")}
                      </span>
                    </div>
                    {item.notes ? (
                      <div className="unverified_row_reason">
                        <span className="reason_tag">Reason:</span>
                        <span className="reason_text">{item.notes}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalIssuesCount === 0 && (
            <div className="unverified_empty_note">
              Checklists are incomplete or not verified for this trip.
            </div>
          )}
        </div>

        {/* Contact Senior Guidance */}
        <div className="unverified_contact_banner">
          <FiPhoneCall className="contact_icon" />
          <div className="contact_text">
            <span>Pre-trip checks must be cleared before dispatch.</span>
            <strong>Please contact your senior supervisor or operations manager for approval.</strong>
          </div>
        </div>

        {/* Senior Override Active Badge (only when senior has permission) */}
        {canStartUnverified && (
          <div className="senior_active_badge">
            <FiCheckCircle />
            <span>Senior Authorization Active: You have permission to override and dispatch.</span>
          </div>
        )}

        {/* Actions */}
        <div className="unverified_modal_actions">
          {canStartUnverified && onProceedOverride ? (
            <>
              <button
                type="button"
                className="unverified_btn_secondary"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
              <button
                type="button"
                className="unverified_btn_primary"
                onClick={onProceedOverride}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    Starting...
                  </>
                ) : (
                  <>
                    <FiPlay className="me-1" />
                    Start Trip (Override)
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="unverified_btn_single"
              onClick={onClose}
              disabled={loading}
            >
              Close
            </button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default UnverifiedTripModal;
